import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Building2, Hash, ShieldCheck, ArrowRight, PackageCheck, Globe } from "lucide-react";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import ChatPanel from "@/components/ChatPanel";
import DocList from "@/components/DocList";
import { Stars, StarRow, RateForm } from "@/components/Rating";
import { StatusBadge } from "@/components/Badges";
import { t } from "@/lib/i18n";

const Row = ({ icon: Icon, label, value, href, testId }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      {href ? <a href={href} className="text-sm font-semibold break-words hover:text-amber-400" data-testid={testId}>{value}</a> : <p className="text-sm font-semibold break-words" data-testid={testId}>{value || "—"}</p>}
    </div>
  </div>
);

function DeliveryCard({ c, onChange }) {
  const done = c.load.status === "completed";
  const deliver = async () => {
    try { await api.post(`/connections/${c.bid.id}/complete`); toast.success(t("Load marked as delivered")); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="hf-card p-5 space-y-4" data-testid="delivery-card">
      <div className="flex justify-between items-center"><p className="hf-label mb-0">{t("Delivery & rating")}</p><StatusBadge status={c.load.status} testId="connection-load-status" /></div>
      {!done && (
        <>
          <p className="text-sm text-slate-600">{t("Once the load has been offloaded, either party can mark it delivered. Both sides can then rate each other.")}</p>
          <button onClick={deliver} className="hf-btn-dark w-full" data-testid="btn-mark-delivered"><PackageCheck className="w-4 h-4" /> {t("Mark load delivered")}</button>
        </>
      )}
      {done && !c.my_rating && <RateForm bidId={c.bid.id} name={c.counterparty.company_name} onDone={onChange} />}
      {done && c.my_rating && (
        <div data-testid="my-rating-given">
          <p className="text-xs text-slate-500 mb-1">{t("You rated")} {c.counterparty.company_name}</p>
          <StarRow value={c.my_rating.stars} size="w-5 h-5" />
          {c.my_rating.comment && <p className="text-sm text-slate-600 mt-1 break-words">"{c.my_rating.comment}"</p>}
        </div>
      )}
      {c.their_rating && (
        <div className="pt-3 border-t" data-testid="their-rating">
          <p className="text-xs text-slate-500 mb-1">{c.counterparty.company_name} {t("rated you")}</p>
          <StarRow value={c.their_rating.stars} size="w-5 h-5" />
          {c.their_rating.comment && <p className="text-sm text-slate-600 mt-1 break-words">"{c.their_rating.comment}"</p>}
        </div>
      )}
    </div>
  );
}

export default function ConnectionDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/connections/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <p className="font-mono text-sm text-slate-500">{t("LOADING…")}</p>;
  const p = c.counterparty;

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-4">
        <div className="rounded-lg bg-slate-900 text-white p-6 hf-rise" data-testid="card-revealed-contact-dossier">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400"><ShieldCheck className="w-3.5 h-3.5" /> {t("Unlocked dossier ·")} {t(p.role)}</p>
          <h1 className="font-display font-black text-2xl mt-3 break-words" data-testid="dossier-company-name">{p.company_name}</h1>
          <p className="text-slate-400 text-sm">{p.name} · {p.public_code}</p>
          <div className="mt-2 text-white"><Stars rating={{ avg: p.rating_avg || 0, count: p.rating_count || 0 }} testId="dossier-rating" /></div>
          <div className="mt-6 space-y-4">
            <Row icon={Phone} label={t("Phone / WhatsApp")} value={p.phone} href={`tel:${p.phone}`} testId="dossier-phone" />
            <Row icon={Mail} label={t("Email")} value={p.email} href={`mailto:${p.email}`} testId="dossier-email" />
            <Row icon={MapPin} label={t("Address")} value={p.address} />
            <Row icon={Building2} label={t("Company reg.")} value={p.registration_number} />
            <Row icon={Hash} label={t("VAT")} value={p.vat_number} />
            <Row icon={Globe} label={t("Country · Currency")} value={[p.country, p.currency].filter(Boolean).join(" · ")} />
            {p.git_insurance && <Row icon={ShieldCheck} label={t("Goods in Transit insurance")} testId="dossier-git" value={`${p.git_insurance.insurer} · Policy ${p.git_insurance.policy_number} · ${money(p.git_insurance.cover_amount, p.git_insurance.currency)} cover · expires ${fmtDate(p.git_insurance.expiry_date)}`} />}
          </div>
        </div>
        <DeliveryCard c={c} onChange={load} />
        <div className="hf-card p-5">
          <p className="hf-label">{t("Registration papers")}</p>
          <DocList docs={p.documents} testPrefix="dossier-doc" />
        </div>
        <Link to={`/loads/${c.load.id}`} className="hf-card p-5 block hover:border-amber-500 transition-colors">
          <p className="hf-label">{t("Agreed load")}</p>
          <p className="font-display font-bold flex flex-wrap items-center gap-2">{c.load.loading_area} <ArrowRight className="w-4 h-4 text-amber-600" /> {c.load.offloading_area}</p>
          <p className="font-mono text-xs text-slate-500 mt-1">{t(c.load.commodity)} · {c.load.tonnage}t · {t(c.load.truck_type)}</p>
          <p className="font-mono text-sm mt-2">{t("Agreed rate")} <b>{money(c.bid.price_per_mt, c.load.currency || "USD")}{t("/mt")}</b> {t("· Start")} {fmtDate(c.load.loading_date)} {t("· Complete")} {fmtDate(c.load.offloading_date)}</p>
        </Link>
      </div>
      <div className="lg:col-span-7 min-w-0">
        <ChatPanel bidId={c.bid.id} />
        <p className="text-xs text-slate-500 mt-3">{t("Mi-Load only connects you. Finalise load planning, waybills and payment terms directly with your counterparty.")}</p>
      </div>
    </div>
  );
}
